import { RealEstateStatusEnum } from '@app-type/RealEstateEnum';
import {
	PropertySubType,
	PropertyType,
	RealEstateDaysOnMarketEnum,
	RealEstateFilter,
	RealEstateSpecialListingConditionsEnum,
	SaleLeaseOptions
} from './realEstate';

export const PROPERTY_TYPE_OPTIONS = Object.values(PropertyType);
export const PROPERTY_SUB_TYPE_OPTIONS = Object.values(PropertySubType);
export const PROPERTY_STATUS_OPTIONS = Object.values(RealEstateStatusEnum);
export const DAYS_ON_MARKET_OPTIONS = Object.values(RealEstateDaysOnMarketEnum);
export const SPECIAL_LISTING_CONDITIONS_OPTIONS = Object.values(RealEstateSpecialListingConditionsEnum);

export const isPropertyType = (item: string): item is PropertyType =>
	PROPERTY_TYPE_OPTIONS.includes(item as PropertyType);

export const isPropertySubType = (item: string): item is PropertySubType =>
	PROPERTY_SUB_TYPE_OPTIONS.includes(item as PropertySubType);

export const isPropertyStatus = (item: string): item is RealEstateStatusEnum =>
	PROPERTY_STATUS_OPTIONS.includes(item as RealEstateStatusEnum);

export const isDaysOnMarket = (item: string): item is RealEstateDaysOnMarketEnum =>
	DAYS_ON_MARKET_OPTIONS.includes(item as RealEstateDaysOnMarketEnum);

export const isSpecialListingCondition = (item: string): item is RealEstateSpecialListingConditionsEnum =>
	SPECIAL_LISTING_CONDITIONS_OPTIONS.includes(item as RealEstateSpecialListingConditionsEnum);

export function normalizeFilterArray(value?: string | string[]): string[] | undefined {
	if (Array.isArray(value)) {
		const normalized = value.map(item => item?.trim()).filter(item => !!item);
		return normalized.length ? normalized : undefined;
	}

	if (typeof value === 'string') {
		const normalized = value
			.split(',')
			.map(item => item.trim())
			.filter(item => !!item);
		return normalized.length ? normalized : undefined;
	}

	return undefined;
}

export function normalizeEnumArray<T extends string>(
	value: T[] | string | string[] | undefined,
	isValid: (item: string) => item is T
): T[] | undefined {
	const normalized = normalizeFilterArray(value as string | string[] | undefined);
	if (!normalized) {
		return undefined;
	}

	const validItems = normalized.filter(isValid);
	return validItems.length ? validItems : undefined;
}

export function normalizeRealEstateFilterCollections(filter: RealEstateFilter): RealEstateFilter {
	return {
		...filter,
		minBathrooms: normalizePositiveNumber(filter.minBathrooms),
		minBedrooms: normalizePositiveNumber(filter.minBedrooms),
		saleLeaseOptions: filter.saleLeaseOptions ?? SaleLeaseOptions.ALL,
		onlyMyListings: filter.onlyMyListings ?? false,
		sellerFinancing: filter.sellerFinancing ?? false,
		shortTermRentalAllowed: filter.shortTermRentalAllowed ?? false,
		propertyType: normalizeEnumArray(filter.propertyType, isPropertyType),
		propertySubTypes: normalizeEnumArray(filter.propertySubTypes, isPropertySubType),
		propertyStatus: normalizeEnumArray(filter.propertyStatus, isPropertyStatus),
		// Empty selection must serialize as [] so APIs (e.g. promoted content save) clear the field instead of omitting it.
		specialListingConditions:
			normalizeEnumArray(filter.specialListingConditions, isSpecialListingCondition) ?? [],
		listOfficeNames: normalizeFilterArray(filter.listOfficeNames),
		features: normalizeFilterArray(filter.features),
		daysOnMarket:
			typeof filter.daysOnMarket === 'string' && isDaysOnMarket(filter.daysOnMarket) ? filter.daysOnMarket : undefined
	};
}

function normalizePositiveNumber(value: string | number | undefined): number | undefined {
	if (value === null || value === undefined || value === '') {
		return undefined;
	}

	const parsed = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}
